import { useCookies } from 'react-cookie'
import ZocksIconBlack from '../assets/images/zocks-icon-black.svg'
import { SocialLogin } from '../views/auth/SocialButtons'
import { useGetSubdomainIdPsQuery } from '../store/idp/idp'
import { useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { addToState } from '../views/auth/utils'

export const useCompanySso = () => {
  const _COMPANY_SPECIFIC_SUBDOMAIN_NAME = 'zocksCompanySpecificSubdomain'
  const _PREFERRED_AUTH_METHOD_NAME = 'zocksPreferredAuthMethod'
  const _PREFERRED_AUTH_METHOD_CANDIDATE_NAME = 'zocksPreferredAuthMethodCandidate'
  const _COGNITO_DOMAIN = process.env.REACT_APP_CONSOLE_COGNITO_DOMAIN
  const [cookies, setCookie, removeCookie] = useCookies([_COMPANY_SPECIFIC_SUBDOMAIN_NAME, _PREFERRED_AUTH_METHOD_NAME, _PREFERRED_AUTH_METHOD_CANDIDATE_NAME])
  const [searchParams] = useSearchParams()
  const tenYearsLater = new Date()
  tenYearsLater.setFullYear(tenYearsLater.getFullYear() + 10)
  const getCookieOptions = () => ({
    path: '/',
    domain: window.location.hostname.endsWith('localhost') ? '.dev.localhost' : '.zocks.io',
    expires: tenYearsLater,
  })

  const getCompanySpecificSubdomain = () => {
    return cookies[_COMPANY_SPECIFIC_SUBDOMAIN_NAME]
  }

  const { data: subdomainIdps } = useGetSubdomainIdPsQuery(getCompanySpecificSubdomain() ? { subdomain: getCompanySpecificSubdomain() } : skipToken)
  const companySpecificIdps = subdomainIdps ? subdomainIdps.data.map((idp): SocialLogin => ({ ...idp, logo: ZocksIconBlack })) : []

  const removeCompanySpecificSubdomain = () => {
    if (getCompanySpecificSubdomain()) {
      removeCookie(_COMPANY_SPECIFIC_SUBDOMAIN_NAME, getCookieOptions())
    }
  }
  const saveCompanySpecificSubdomain = (companySpecificSubdomain: string) => {
    if (getCompanySpecificSubdomain() !== companySpecificSubdomain) {
      setCookie(_COMPANY_SPECIFIC_SUBDOMAIN_NAME, companySpecificSubdomain, getCookieOptions())
    }
  }
  const getPreferredAuthMethod = () => {
    return cookies[_PREFERRED_AUTH_METHOD_NAME]
  }
  const savePreferredAuthMethod = (identityProvider: string) => {
    if (getPreferredAuthMethod() !== identityProvider) {
      setCookie(_PREFERRED_AUTH_METHOD_NAME, identityProvider, getCookieOptions())
    }
  }
  const savePreferredAuthMethodCandidate = (identityProvider: string) => {
    if (shouldUseCompanySso() && getPreferredAuthMethodCandidate() != identityProvider) {
      setCookie(_PREFERRED_AUTH_METHOD_CANDIDATE_NAME, identityProvider, getCookieOptions())
    }
  }
  const getPreferredAuthMethodCandidate = () => {
    return cookies[_PREFERRED_AUTH_METHOD_CANDIDATE_NAME]
  }

  const shouldUseCompanySso = (): boolean => {
    return !!getCompanySpecificSubdomain()
  }

  const handleCompanySpecificSubdomain = () => {
    const hostname = window.location.hostname
    if (hostname === 'localhost') {
      return
    }
    if (hostname.startsWith('console.')) {
      if (!searchParams.get('code') && searchParams.get('sourceClient') !== 'academy') {
        removeCompanySpecificSubdomain()
      }
    } else {
      const companySpecificSubdomain = hostname.substring(0, hostname.indexOf('.'))
      saveCompanySpecificSubdomain(companySpecificSubdomain)
    }
  }

  const getCompanySpecificIdPs = (): SocialLogin[] => (shouldUseCompanySso() ? companySpecificIdps || [] : [])

  const handlePreferredAuthMethod = () => {
    if (shouldUseCompanySso() && getPreferredAuthMethodCandidate()) {
      savePreferredAuthMethod(getPreferredAuthMethodCandidate())
    }
  }

  const handlePreferredAuthMethodRedirect = (): boolean => {
    const preferredAuthMethod = getPreferredAuthMethod()
    if (!shouldUseCompanySso() || !preferredAuthMethod) {
      return false
    }
    const idps = getCompanySpecificIdPs()
    const idp = idps.find((idp) => idp.identityProvider === preferredAuthMethod)
    if (!idp) {
      return false
    }
    let state = searchParams.get('state')
    const sourceClient = searchParams.get('sourceClient')
    sourceClient && (state = addToState(state, "sourceClient", sourceClient))
    const stateQuery = state ? `&state=${state}` : ''

    const redirectSearchParams = `identity_provider=${idp.identityProvider}&redirect_uri=${idp.redirectUri}&response_type=${idp.responseType}&client_id=${idp.clientId}&scope=${idp.scope}${stateQuery}`
    window.location.href = `${_COGNITO_DOMAIN}?${redirectSearchParams}`
    return true
  }

  return {
    shouldUseCompanySso,
    handleCompanySpecificSubdomain,
    savePreferredAuthMethodCandidate,
    getCompanySpecificIdPs,
    handlePreferredAuthMethod,
    handlePreferredAuthMethodRedirect,
  }
}
